import { DateHelpers } from "../helpers/date-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { AiStrategyTradeModel } from "../models/ai-strategy-trade/ai-strategy-trade.model";
import { MarketingAISignalModel } from "../models/marketing-ai-signal/marketing-ai-signal.model";
import { IComponent } from "./component.interface";
import { FormatHelpers } from "../helpers/format-helpers";

export class MarketingAISignalComponent implements IComponent {
    public id = '';
    private readonly arrowAnimationDuration = 0.9;
    private readonly arrowLineWidth = 3;
    private readonly minElementGap = 20;
    private element: HTMLElement | null = null;
    private entryCalloutEl: HTMLElement | null = null;
    private maxGainEl: HTMLElement | null = null;
    private premiumBadgeEl: HTMLElement | null = null;
    private arrowPathEl: SVGPathElement | null = null;
    private arrowPolygonEl: SVGPolygonElement | null = null;
    private model: MarketingAISignalModel | null = null;
    private base: IChartContainer;
    private allTrades: AiStrategyTradeModel[] = [];
    private trade: AiStrategyTradeModel | null = null;
    private headerBannerEl: HTMLElement | null = null;
    private footerBannerEl: HTMLElement | null = null;
    private sidebarBannerEl: HTMLElement | null = null;
    private outerContainerEl: HTMLElement | null = null;
    public isBannerActive = false;
    public showSidebar = false;
    private readonly entryViewOffset = 0.3;
    private didFrameTrade = false;
    private framedAtChartWidth: number | null = null;
    private changeListeners: (() => void)[] = [];

    public onChange(callback: () => void) {
        this.changeListeners.push(callback);
    }

    constructor(base: IChartContainer) {
        this.base = base;
        this.createElement();
        this.subscribeChartEvents();
        this.subscribeChartData();
    }

    public setData(model: MarketingAISignalModel) {
        this.model = model;
        this.render();

        // render() just toggled the banner grid classes, resizing chart-container/main-chart;
        // reflow now so toPixels() below reflects the new size, not the pre-resize one.
        this.base.tiChartSeries?.highStockChart?.reflow();

        this.didFrameTrade = false;
        this.framedAtChartWidth = this.base.tiChartSeries?.highStockChart?.chartWidth ?? null;
        this.frameTradeOnLoad();
        this.positionEntryCallout();
        this.positionMaxGain();
        this.positionArrow(true);
        this.changeListeners.forEach(cb => cb());

        // Re-run after first paint so offsetHeight is accurate for the clamp in positionMaxGain
        requestAnimationFrame(() => {
            this.positionEntryCallout();
            this.positionMaxGain();
            this.positionArrow(false);
        });

        // Late layout shifts (banner images/web fonts finishing load) can still resize the
        // container after the above; reflow and reposition once more as a safety net.
        setTimeout(() => {
            this.base.tiChartSeries?.highStockChart?.reflow();
            this.positionEntryCallout();
            this.positionMaxGain();
            this.positionArrow(false);
        }, 1500);
    }

    public getAllTrades(): AiStrategyTradeModel[] {
        return this.allTrades;
    }

    public getMatchTrade(): AiStrategyTradeModel | null {
        return this.trade;
    }

    private createElement() {
        this.element = document.createElement('div');
        this.element.className = 'marketing-ai-signal hidden';
        const mainChart = this.base.element.findFirst('.main-chart');
        mainChart?.insertAdjacentElement('beforeend', this.element);

        const chartContainer = mainChart?.parentElement;
        this.outerContainerEl = chartContainer?.parentElement ?? null;

        this.headerBannerEl = document.createElement('div');
        this.headerBannerEl.className = 'marketing-ai-signal__header-banner hidden';
        chartContainer?.insertAdjacentElement('beforebegin', this.headerBannerEl);

        this.footerBannerEl = document.createElement('div');
        this.footerBannerEl.className = 'marketing-ai-signal__footer-banner hidden';
        chartContainer?.insertAdjacentElement('afterend', this.footerBannerEl);

        if (this.showSidebar) {
            this.sidebarBannerEl = document.createElement('div');
            this.sidebarBannerEl.className = 'marketing-ai-signal__sidebar-banner hidden';
            chartContainer?.insertAdjacentElement('beforebegin', this.sidebarBannerEl);
        }

        this.entryCalloutEl = this.element.querySelector('.marketing-ai-signal__entry-callout');
        this.maxGainEl = this.element.querySelector('.marketing-ai-signal__max-gain');
    }

    private subscribeChartEvents() {
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            // A width change (e.g. resizing into mobile after loading desktop-wide) can put
            // the entry bar out of view even though a prior call already framed it once at
            // the old width, so re-arm the one-shot latch whenever the width actually moves.
            const chart = this.base.tiChartSeries?.highStockChart;
            if (chart && chart.chartWidth !== this.framedAtChartWidth) {
                this.framedAtChartWidth = chart.chartWidth;
                this.didFrameTrade = false;
            }

            this.frameTradeOnLoad();
            this.positionEntryCallout();
            this.positionMaxGain();
            this.positionArrow(false);
        });
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.positionEntryCallout();
            this.positionMaxGain();
            this.positionArrow(false);
        });
    }

    private subscribeChartData() {
        this.base.chartService.subscribe_MarketingAISignalTradesData((model: Array<AiStrategyTradeModel>, symbol: string, entryDateTime: Date | null, aiStrategyId: string) => {
            this.applyFromTrades(model, symbol, entryDateTime, aiStrategyId);
        });
        this.base.chartService.subscribeChartStyleData(() => {
            if (this.isBannerActive) {
                this.setBgColor();
            }
        });
    }

    private render() {
        if (!this.element || !this.model) return;
        this.element.innerHTML = this.template();
        this.entryCalloutEl = this.element.querySelector('.marketing-ai-signal__entry-callout');
        this.maxGainEl = this.element.querySelector('.marketing-ai-signal__max-gain');
        this.premiumBadgeEl = this.element.querySelector('.marketing-ai-signal__premium-badge');
        this.arrowPathEl = this.element.querySelector('.marketing-ai-signal__arrow-path');
        this.arrowPolygonEl = this.element.querySelector('.marketing-ai-signal__arrow-polygon');
        this.element.style.opacity = '0';
        this.element.classList.remove('hidden');

        if (this.headerBannerEl) {
            const basePath = this.base.basePath;
            const symbol = this.base.tiChartSeries?.symbol || '';
            const pnl = '';
            const pnlSign = this.model.exitPercentage >= 0 ? '+' : '';
            this.headerBannerEl.innerHTML = `
                <a class="marketing-ai-signal__header-banner-content no-d" href="https://trade-ideas.com/features/ai-signals/" target="_blank">
                    <div class="marketing-ai-signal__header-arrows marketing-ai-signal__header-arrows--right">
                        <img class="marketing-ai-signal__header-arrow" src="${basePath}assetfiles/img/marketing-ai-signal/arrow-v2.svg">
                        <img class="marketing-ai-signal__header-arrow" src="${basePath}assetfiles/img/marketing-ai-signal/arrow-v2.svg">
                        <img class="marketing-ai-signal__header-arrow" src="${basePath}assetfiles/img/marketing-ai-signal/arrow-v2.svg">
                    </div>
                    <span class="marketing-ai-signal__header-text">HOLLY AI TRADE WINNER!&nbsp;<span class="marketing-ai-signal__header-highlight">$${symbol} ${pnlSign}${pnl}%</span></span>
                    <img class="marketing-ai-signal__header-fire" src="${basePath}assetfiles/img/marketing-ai-signal/fire-v2.svg">
                    <div class="marketing-ai-signal__header-arrows">
                        <img class="marketing-ai-signal__header-arrow" src="${basePath}assetfiles/img/marketing-ai-signal/arrow-v2.svg">
                        <img class="marketing-ai-signal__header-arrow" src="${basePath}assetfiles/img/marketing-ai-signal/arrow-v2.svg">
                        <img class="marketing-ai-signal__header-arrow" src="${basePath}assetfiles/img/marketing-ai-signal/arrow-v2.svg">
                    </div>
                </a>
                <button class="marketing-ai-signal__header-close no-d">
                    <svg class="marketing-ai-signal__header-close-icon">
                        <use xlink:href="${basePath}assetfiles/img/marketing-ai-signal/close-btn.svg#close-btn-obj"></use>
                    </svg>
                </button>
            `;
            this.headerBannerEl.classList.remove('hidden');
            this.headerBannerEl.querySelector('.marketing-ai-signal__header-close')
                ?.addEventListener('click', () => this.hide());
        }

        if (this.footerBannerEl && this.base.isDemo) {
            const basePath = this.base.basePath;
            this.footerBannerEl.innerHTML = `
                <a class="marketing-ai-signal__footer-inner no-d" href="https://trade-ideas.com/pricing/" target="_blank">
                    <div class="marketing-ai-signal__footer-side marketing-ai-signal__footer-side--left">
                        <img class="marketing-ai-signal__footer-rocket-icon" src="${basePath}assetfiles/img/marketing-ai-signal/rocket-v2.svg">
                    </div>
                    <div class="marketing-ai-signal__footer-center">
                        <div class="marketing-ai-signal__footer-main-row">
                            <span class="marketing-ai-signal__footer-main-text">DON'T MISS THE NEXT HOLLY AI ALERT!&nbsp;&nbsp;<span class="marketing-ai-signal__footer-subscribe">SUBSCRIBE NOW!</span></span>
                        </div>
                        <div class="marketing-ai-signal__footer-separator"></div>
                        <div class="marketing-ai-signal__footer-items-row">
                            <span class="marketing-ai-signal__footer-item"><img class="marketing-ai-signal__footer-check-icon" src="${basePath}assetfiles/img/marketing-ai-signal/check-v2.svg">&nbsp;AI-POWERED SIGNALS</span>
                            <span class="marketing-ai-signal__footer-item"><img class="marketing-ai-signal__footer-check-icon" src="${basePath}assetfiles/img/marketing-ai-signal/check-v2.svg">&nbsp;PROVEN PERFORMANCE</span>
                            <span class="marketing-ai-signal__footer-item"><img class="marketing-ai-signal__footer-check-icon" src="${basePath}assetfiles/img/marketing-ai-signal/check-v2.svg">&nbsp;REAL-TIME ALERTS</span>
                            <span class="marketing-ai-signal__footer-item"><img class="marketing-ai-signal__footer-check-icon" src="${basePath}assetfiles/img/marketing-ai-signal/check-v2.svg">&nbsp;MAXIMIZE YOUR EDGE</span>
                        </div>
                    </div>
                    <div class="marketing-ai-signal__footer-side marketing-ai-signal__footer-side--right">
                        <img class="marketing-ai-signal__footer-zigzag-icon" src="${basePath}assetfiles/img/marketing-ai-signal/zigzag-v2.svg">
                    </div>
                </a>
            `;
            this.footerBannerEl.classList.remove('hidden');
        }

        if (this.showSidebar && this.sidebarBannerEl) {
            this.sidebarBannerEl.innerHTML = this.sidebarTemplate();
            this.sidebarBannerEl.classList.remove('hidden');
            this.outerContainerEl?.classList.add('ai-signal-sidebar');
        }

        this.setBgColor();
        this.outerContainerEl?.classList.add('ai-signal-banner');
        this.isBannerActive = true;
    }

    private setBgColor() {
        if (this.base.tiChartSeries?.chartStyleSettings) {
            let bgColor = '';
            const percent = this.base.tiChartSeries.chartStyleSettings.singleStockBGPercent;
            if (this.base.tiChartSeries.chartStyleSettings.singleStockInfoBackgroundColor) {
                bgColor = this.base.tiChartSeries.chartStyleSettings.singleStockInfoBackgroundColor.toRGB();
            } else {
                if (this.base.tiChartSeries.chartStyleSettings.isDarkTheme) {
                    bgColor = this.base.tiChartSeries.chartStyleSettings.singleStockBaseColor?.toLowerCase() == 'gradient' ? this.base.tiChartSeries.chartStyleSettings.selectedChartGradientColor.lighten(percent) : this.base.tiChartSeries.chartStyleSettings.selectedChartBackgroundColor.lighten(percent);
                } else {
                    bgColor = this.base.tiChartSeries.chartStyleSettings.singleStockBaseColor?.toLowerCase() == 'gradient' ? this.base.tiChartSeries.chartStyleSettings.selectedChartGradientColor.darken(percent) : this.base.tiChartSeries.chartStyleSettings.selectedChartBackgroundColor.darken(percent);
                }
            }

            if (this.headerBannerEl) this.headerBannerEl.style.backgroundColor = bgColor;
            if (this.footerBannerEl) this.footerBannerEl.style.backgroundColor = bgColor;
            if (this.sidebarBannerEl) this.sidebarBannerEl.style.backgroundColor = bgColor;
        }
    }

    public hide() {
        this.element?.classList.add('hidden');
        this.entryCalloutEl?.classList.add('hidden');
        this.maxGainEl?.classList.add('hidden');
        this.premiumBadgeEl?.classList.add('hidden');
        if (this.arrowPathEl) this.arrowPathEl.setAttribute('d', '');
        if (this.arrowPolygonEl) this.arrowPolygonEl.setAttribute('opacity', '0');
        this.headerBannerEl?.classList.add('hidden');
        this.footerBannerEl?.classList.add('hidden');
        this.sidebarBannerEl?.classList.add('hidden');
        this.outerContainerEl?.classList.remove('ai-signal-banner');
        this.outerContainerEl?.classList.remove('ai-signal-sidebar');
        this.isBannerActive = false;
        this.changeListeners.forEach(cb => cb());
        window.dispatchEvent(new Event('resize'));
    }

    private sidebarTemplate(): string {
        const items = [
            { color: '#4A9EF0', label: 'AI-Powered<br>Trade Signals', sub: 'Precision alerts driven by AI', icon: '<path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 3a7 7 0 1 1 0 14A7 7 0 0 1 12 5zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" fill="currentColor"/>' },
            { color: '#39FF14', label: 'Proven Results', sub: 'Winning trades. Real performance.', icon: '<polyline points="3,17 8,10 13,14 20,5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><polyline points="17,5 20,5 20,8" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' },
            { color: '#4A9EF0', label: 'Early Advantage', sub: 'Get in early. Stay ahead.', icon: '<path d="M12 2L4 6v6c0 5.25 3.5 10.15 8 11.5C16.5 22.15 20 17.25 20 12V6l-8-4z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>' },
            { color: '#FFB800', label: 'Actionable<br>Insights', sub: 'Clear signals. Confident decisions.', icon: '<polygon points="13,2 13,10 18,10 11,22 11,14 6,14" fill="currentColor"/>' },
            { color: '#9B59B6', label: 'Premium Access', sub: 'Exclusive tools for serious traders.', icon: '<polygon points="12,2 15.5,8.5 22,9.5 17,14.5 18.5,21 12,17.5 5.5,21 7,14.5 2,9.5 8.5,8.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>' },
        ];
        return items.map(({ color, label, sub, icon }) => `
            <div class="marketing-ai-signal__sidebar-item">
                <div class="marketing-ai-signal__sidebar-icon" style="color:${color};border-color:${color}33;background:${color}1A">
                    <svg viewBox="0 0 24 24" width="22" height="22">${icon}</svg>
                </div>
                <div class="marketing-ai-signal__sidebar-text">
                    <span class="marketing-ai-signal__sidebar-title" style="color:${color}">${label}</span>
                    <span class="marketing-ai-signal__sidebar-subtitle">${sub}</span>
                </div>
            </div>
        `).join('');
    }

    private template(): string {
        if (!this.model) return '';

        const exitLabel = `MAX GAIN`;
        const entryTitle = `HOLLY AI ENTRY SIGNAL 📈`;
        // const exitLabel = `${this.model.exitDescription || 'MAX GAIN'}`;
        // const entryTitle = this.model?.entryDescription;
        const percentLabel = `${Math.abs(this.model.exitPercentage)}%`;
        const entryDate = this.model?.entryDate;
        const entryPrice = this.model.entryPrice;

        return `
            <svg class="marketing-ai-signal__arrow-svg" overflow="visible">
                <path class="marketing-ai-signal__arrow-path" fill="none" stroke="#89F6FF" stroke-width="${this.arrowLineWidth}"/>
                <polygon class="marketing-ai-signal__arrow-polygon" points="0 0, 12 4, 0 8" fill="#89F6FF" opacity="0"/>
            </svg>
            <div class="marketing-ai-signal__premium-badge">
                <div class="inner-border">
                    <span>PREMIUM</span>
                    <span>SUBSCRIBERS</span>
                    <span>GOT IN</span>
                    <span>EARLY!</span>
                </div>
            </div>
            <div class="marketing-ai-signal__max-gain">
                <div class="marketing-ai-signal__max-gain-container">
                    <span class="marketing-ai-signal__max-gain-label">${exitLabel}</span>
                    <span class="marketing-ai-signal__max-gain-value">${percentLabel}</span>
                </div>
            </div>
            <div class="marketing-ai-signal__entry-callout">
                <div class="marketing-ai-signal__entry-callout-header">
                    <span class="marketing-ai-signal__entry-callout-title">${entryTitle}</span>
                </div>
                <div class="marketing-ai-signal__entry-callout-body">
                    <span class="marketing-ai-signal__entry highlight">${entryDate}</span>
                    <span class="marketing-ai-signal__entry"> AT </span>
                    <span class="marketing-ai-signal__entry highlight">${entryPrice}</span>
                    <span class="marketing-ai-signal__entry highlight"> - BUY HERE</span>
                </div>
            </div>
        `;
    }

    // A narrow chart fits fewer bars, so the default right-anchored view can start after
    // the trade entirely. Frame it once on load; later panning is the user's.
    private frameTradeOnLoad() {
        if (this.didFrameTrade || !this.model) return;
        const tiChartSeries = this.base.tiChartSeries;
        const chart = tiChartSeries?.highStockChart;
        if (!chart || !tiChartSeries) return;

        const entryDate = this.parseDateTime(this.model.entryDate, this.model.entryTime);
        const points = tiChartSeries.tiChartPoints;
        const point = points.find(p => p.start.valueOf() >= entryDate.valueOf());
        if (!point) return;

        // Decision made for this trade either way, so a later user pan is not undone.
        this.didFrameTrade = true;

        const extremes = chart.xAxis[0].getExtremes();
        const isEntryVisible = point.xValue >= extremes.min && point.xValue <= extremes.max;
        if (isEntryVisible) return;

        // Same zoom level, just shifted so the entry bar sits near the left with the run-up after it.
        const span = extremes.max - extremes.min;
        const newMin = point.xValue - span * this.entryViewOffset;
        tiChartSeries.setExtremes(newMin, newMin + span);
    }

    private positionEntryCallout() {
        if (!this.model || !this.entryCalloutEl) return;
        const tiChartSeries = this.base.tiChartSeries;
        const chart = tiChartSeries?.highStockChart;
        if (!chart || !tiChartSeries) return;

        const entryDate = this.parseDateTime(this.model.entryDate, this.model.entryTime);
        const points = tiChartSeries.tiChartPoints;
        const point = points.find(p => p.start.valueOf() >= entryDate.valueOf())
            ?? points[points.length - 1];
        if (!point) return;

        // Only reveal once the entry bar is actually plotted in the series.
        const mainSerie: any = chart.series?.[0];
        const isRendered = !!mainSerie?.processedXData?.some((x: number) => x == point.xValue);
        if (!isRendered) return;

        const arrowWidth = 16;
        const entryPx = chart.xAxis[0].toPixels(point.xValue, false);
        const yPixel = chart.yAxis[0].toPixels(this.model.entryPrice, false);

        if (isNaN(entryPx) || isNaN(yPixel)) return;

        // Default: callout sits to the LEFT of the entry bar (arrow points right).
        // On a narrow chart neither side fits, so take whichever has more room.
        const calloutW = this.entryCalloutEl.offsetWidth;
        const roomLeft = entryPx - arrowWidth - this.minElementGap;
        const roomRight = chart.chartWidth - entryPx - arrowWidth - this.minElementGap;
        const fitsLeft = calloutW <= roomLeft;
        const flip = !fitsLeft && roomRight > roomLeft;
        this.entryCalloutEl.classList.toggle('marketing-ai-signal__entry-callout--flipped', flip);

        // Callout arrow should reach the bar's edge, not its center.
        const barPoint = mainSerie?.points?.find((p: any) => p.x == point.xValue);
        const barW = barPoint?.shapeArgs?.width ?? 0;
        const gap = arrowWidth + barW + 4; // +4 for the bar's border width; bump this to widen the gap
        this.entryCalloutEl.style.left = `${flip ? entryPx + barW : entryPx - gap}px`;
        // this.entryCalloutEl.style.left = `${entryPx + arrowWidth}px`;

        // Clamp into the PRICE PANE's own bounds, not the whole (multi-pane) chart's — this
        // is a stock chart with a volume pane stacked below, so chart.plotTop/plotHeight span
        // both panes, and clamping against that still let an out-of-range entry price land in
        // the volume pane's territory, right next to the footer banner.
        const priceAxis: any = chart.yAxis[0];
        const calloutH = this.entryCalloutEl.offsetHeight || 0;
        const minTop = priceAxis.top + calloutH / 2 + this.minElementGap;
        const maxTop = priceAxis.top + priceAxis.len - calloutH / 2 - this.minElementGap;
        this.entryCalloutEl.style.top = `${Math.min(Math.max(yPixel, minTop), maxTop)}px`;

        // Valid position against a loaded, rendered data point — safe to show now.
        if (this.element) this.element.style.opacity = '1';
    }

    // Header banner pnl mirrors the bar-derived max gain (this.model.exitPercentage),
    // which positionMaxGain recomputes on every redraw.
    private updateHeaderPnl() {
        if (!this.model || !this.headerBannerEl) return;
        const highlight = this.headerBannerEl.querySelector('.marketing-ai-signal__header-highlight');
        if (!highlight) return;
        const symbol = this.base.tiChartSeries?.symbol || '';
        const pnl = FormatHelpers.numberToStringThousandSeparators(Math.abs(this.model.exitPercentage), true, 2);
        const pnlSign = this.model.exitPercentage >= 0 ? '+' : '';
        highlight.textContent = `$${symbol} ${pnlSign}${pnl}%`;
    }

    private positionMaxGain() {
        if (!this.model || !this.maxGainEl) return;
        const tiChartSeries = this.base.tiChartSeries;
        const chart = tiChartSeries?.highStockChart;
        if (!chart || !tiChartSeries) return;

        const points = tiChartSeries.tiChartPoints;
        if (!points.length) return;

        // Max gain = highest close at/after entry vs the entry bar's close.
        const entryDate = this.parseDateTime(this.model.entryDate, this.model.entryTime);
        const entryIdx = points.findIndex(p => p.start.valueOf() >= entryDate.valueOf());
        const entryPoint = entryIdx >= 0 ? points[entryIdx] : points[0];

        // Anchor the max-gain marker to the highest-close bar from entry onward.
        let point = entryPoint;
        for (let i = Math.max(entryIdx, 0); i < points.length; i++) {
            if (points[i].high > point.high) point = points[i];
        }

        if (this.model.entryPrice && point) {
            this.model.exitPercentage = parseFloat(
                (((point.high - this.model.entryPrice) / this.model.entryPrice) * 100).toFixed(2));
            const valueEl = this.maxGainEl.querySelector('.marketing-ai-signal__max-gain-value');
            if (valueEl) valueEl.textContent = `${Math.abs(this.model.exitPercentage)}%`;
            this.updateHeaderPnl();
        }

        const xPixel = chart.xAxis[0].toPixels(point.xValue, false);
        const yPixel = chart.yAxis[0].toPixels(point.high, false);

        if (isNaN(xPixel) || isNaN(yPixel)) return;

        const elWidth = this.maxGainEl.offsetWidth;
        const elHeight = this.maxGainEl.offsetHeight || 32;

        // Natural position: above last bar's high
        let top = yPixel - elHeight - 10;

        // Clamp: max gain must always sit above the entry callout with gap.
        // entry callout uses transform translate(-100%, -50%), so style.top = vertical center.
        if (this.entryCalloutEl) {
            const calloutRefTop = parseFloat(this.entryCalloutEl.style.top);
            if (!isNaN(calloutRefTop)) {
                const calloutH = this.entryCalloutEl.offsetHeight || 60;
                const calloutTop = calloutRefTop - calloutH / 2;
                const clampedTop = calloutTop - this.minElementGap - elHeight;
                if (top > clampedTop) top = clampedTop;
            }
        }

        // Clamp into the price pane's own bounds, for the same reason positionEntryCallout does.
        const priceAxis: any = chart.yAxis[0];
        const minTop = priceAxis.top + this.minElementGap;
        const maxTop = priceAxis.top + priceAxis.len - elHeight - this.minElementGap;
        top = Math.min(Math.max(top, minTop), maxTop);

        const maxLeft = Math.max(0, chart.chartWidth - elWidth);
        const left = Math.min(Math.max(xPixel - elWidth / 2, 0), maxLeft);
        this.maxGainEl.style.left = `${left}px`;
        this.maxGainEl.style.top = `${top}px`;
    }

    private positionArrow(animate: boolean) {
        if (!this.model || !this.entryCalloutEl || !this.maxGainEl || !this.arrowPathEl) return;

        const refX = parseFloat(this.entryCalloutEl.style.left);
        const refY = parseFloat(this.entryCalloutEl.style.top);
        if (isNaN(refX) || isNaN(refY)) return;

        const maxLeft = parseFloat(this.maxGainEl.style.left);
        const maxTop = parseFloat(this.maxGainEl.style.top);
        const maxH = this.maxGainEl.offsetHeight;
        if (isNaN(maxLeft) || isNaN(maxTop)) return;

        // Start: top-center of entry callout. style.top = vertical center (translateY(-50%)).
        // style.left is the right edge normally, or the left edge when flipped to the right.
        const flipped = this.entryCalloutEl.classList.contains('marketing-ai-signal__entry-callout--flipped');
        const calloutW = this.entryCalloutEl.offsetWidth;
        const calloutH = this.entryCalloutEl.offsetHeight;
        const startX = flipped ? refX + calloutW / 2 : refX - calloutW / 2;
        const startY = refY - calloutH / 2;

        // Arrowhead tip: left-center of max gain box
        const tipX = maxLeft;
        const tipY = maxTop + maxH / 2;

        // Hide arrow if either endpoint is outside the visible chart area
        const parent = this.element?.parentElement;
        if (parent) {
            const pw = parent.offsetWidth;
            const ph = parent.offsetHeight;
            const pad = 50;
            if (startX < -pad || startX > pw + pad ||
                startY < -pad || startY > ph + pad ||
                tipX < -pad || tipX > pw + pad ||
                tipY < -pad || tipY > ph + pad) {
                this.arrowPathEl.setAttribute('d', '');
                if (this.arrowPolygonEl) this.arrowPolygonEl.setAttribute('opacity', '0');
                return;
            }
        }

        // Depart straight up from start, sweep right to arrive horizontally at tip
        const cp1X = startX;
        const cp1Y = tipY;
        const cp2X = tipX - Math.abs(tipX - startX) * 0.45;
        const cp2Y = tipY;

        // Tangent angle at tip (direction cp2 → tip, approx 0°)
        const angleRad = Math.atan2(tipY - cp2Y, tipX - cp2X);
        const angleDeg = angleRad * 180 / Math.PI;

        // Path ends at arrowhead base so stroke doesn't overlap arrowhead body
        const arrowLen = 12;
        const pathEndX = tipX - arrowLen * Math.cos(angleRad);
        const pathEndY = tipY - arrowLen * Math.sin(angleRad);

        this.arrowPathEl.setAttribute('d',
            `M ${startX},${startY} C ${cp1X},${cp1Y} ${cp2X},${cp2Y} ${pathEndX},${pathEndY}`);

        if (this.arrowPolygonEl) {
            this.arrowPolygonEl.setAttribute('transform',
                `translate(${tipX - 12},${tipY - 4}) rotate(${angleDeg},12,4)`);
        }

        if (animate) {
            const len = this.arrowPathEl.getTotalLength();
            this.arrowPathEl.style.strokeDasharray = `${len}`;
            this.arrowPathEl.style.strokeDashoffset = `${len}`;
            this.arrowPathEl.style.setProperty('--ai-arrow-duration', `${this.arrowAnimationDuration}s`);
            this.arrowPathEl.classList.remove('animate');
            void this.arrowPathEl.getBBox();
            this.arrowPathEl.classList.add('animate');

            if (this.arrowPolygonEl) {
                const delay = this.arrowAnimationDuration * 0.5;
                this.arrowPolygonEl.setAttribute('opacity', '0');
                this.arrowPolygonEl.style.setProperty('--ai-arrow-delay', `${delay}s`);
                this.arrowPolygonEl.classList.remove('animate');
                void this.arrowPolygonEl.getBBox();
                this.arrowPolygonEl.classList.add('animate');
            }
        } else {
            // Clear any stale animation state so the full repositioned path renders immediately
            this.arrowPathEl.classList.remove('animate');
            this.arrowPathEl.style.strokeDasharray = '';
            this.arrowPathEl.style.strokeDashoffset = '';
            if (this.arrowPolygonEl) {
                this.arrowPolygonEl.setAttribute('opacity', '1');
                this.arrowPolygonEl.classList.remove('animate');
            }
        }
    }

    public filterTrade(symbol: string, entryDateTime: Date | null, aiStrategyId: string): AiStrategyTradeModel | null {
        const hasValidTime = entryDateTime && !isNaN(entryDateTime.valueOf());
        if (!this.allTrades?.length || !symbol || (!hasValidTime && !aiStrategyId)) {
            return null;
        }

        const targetTime = entryDateTime ? new Date(entryDateTime.getTime()) : null;
        if (targetTime) {
            targetTime.setSeconds(0, 0);
            targetTime.setMilliseconds(0);
        }

        for (const t of this.allTrades) {
            let entryTime: Date | null = null;
            if (t.entryTime instanceof Date) {
                entryTime = new Date(t.entryTime.getTime());
                entryTime.setSeconds(0, 0);
                entryTime.setMilliseconds(0);
            }

            const symbolMatch = t.symbol === symbol;
            const timeMatch = !!entryTime && !!targetTime && entryTime.valueOf() === targetTime.valueOf();
            const aiStrategyIdMatch = !!t.id && !!aiStrategyId && t.id.toLowerCase() === aiStrategyId.toLowerCase();

            // console.log('[MarketingAISignalComponent] filterTrade', { trade: t, symbolMatch, timeMatch, aiStrategyMatch: aiStrategyIdMatch, targetTime: targetTime, targetTimevalueOf: targetTime.valueOf(), entryTime: entryTime, entryTimevalueOf: entryTime?.valueOf() });
            if (symbolMatch && (timeMatch || aiStrategyIdMatch)) return t;
        }
        return null;
    }

    private toModelFromTrade(trade: AiStrategyTradeModel): MarketingAISignalModel {
        const model = new MarketingAISignalModel();
        model.entryDescription = trade.strategyName ?? '';
        if (trade.entryTime instanceof Date) {
            model.entryDate = DateHelpers.format('mm/dd/yyyy', trade.entryTime);
            model.entryTime = DateHelpers.format('HH:MM:SS', trade.entryTime);
        }
        model.entryPrice = typeof trade.entryPrice === 'number' ? trade.entryPrice : 0;

        const hasClosedExit =
            trade.exitTime instanceof Date &&
            typeof trade.exitPrice === 'number' &&
            typeof trade.entryPrice === 'number' &&
            trade.entryPrice !== 0;

        if (hasClosedExit) {
            model.exitDescription = trade.exitReason ?? '';
            model.exitDate = DateHelpers.format('mm/dd/yyyy', trade.exitTime);
            model.exitTime = DateHelpers.format('HH:MM:SS', trade.exitTime);
            const sign = trade.isLong ? 1 : -1;
            model.exitPercentage =
                parseFloat((((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100 * sign).toFixed(2));
        }

        return model;
    }

    public applyFromTrades(trades: AiStrategyTradeModel[], symbol: string, entryDateTime: Date | null, aiStrategyId: string): void {
        this.allTrades = trades;
        this.trade = this.filterTrade(symbol, entryDateTime, aiStrategyId);
        // console.log('[MarketingAISignalComponent] applyFromTrades', { symbol, entryDateTime, aiStrategyId, trade: this.trade, allTrades: this.allTrades });
        if (!this.trade) return;
        this.setData(this.toModelFromTrade(this.trade));
    }

    private parseDateTime(dateStr: string, timeStr?: string): Date {
        const parts = dateStr.split('/').map(Number);
        const date = new Date(parts[2], parts[0] - 1, parts[1]);
        if (timeStr) {
            const timeParts = timeStr.split(':').map(Number);
            date.setHours(timeParts[0] || 0, timeParts[1] || 0, timeParts[2] || 0);
        }
        return date;
    }
}
